import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
export function CeoSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="relative py-20 lg:py-28 bg-white overflow-hidden" ref={ref} data-id="element-101">
      <div className="absolute inset-0 pointer-events-none" data-id="element-102">
        <div className="absolute top-20 left-0 w-[400px] h-[400px] rounded-full bg-primary-50/30 blur-3xl" data-id="element-103" />
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] rounded-full bg-cyan-50/20 blur-3xl" data-id="element-104" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-105">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-center" data-id="element-106">
          {/* Photo / Avatar side */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} className="lg:col-span-2" data-id="element-107">
            <div className="relative max-w-sm mx-auto lg:mx-0" data-id="element-108">
              <div className="aspect-[4/5] rounded-3xl bg-gradient-to-br from-navy-800 via-navy-900 to-navy-950 overflow-hidden relative shadow-2xl shadow-navy-900/30" data-id="element-109">
                <div className="absolute inset-0 opacity-[0.03]" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`
              }} data-id="element-110" />
                <div className="absolute inset-0" data-id="element-111">
                  <div className="absolute top-6 right-6 w-40 h-40 rounded-full bg-primary-500/8 blur-2xl" data-id="element-112" />
                  <div className="absolute bottom-10 left-4 w-28 h-28 rounded-full bg-primary-400/8 blur-2xl" data-id="element-113" />
                  <motion.div animate={{
                  y: [0, -8, 0],
                  rotate: [0, 3, 0]
                }} transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="absolute top-10 right-10 w-12 h-12 border border-primary-500/15 rounded-xl" data-id="element-114" />
                  <motion.div animate={{
                  y: [0, 6, 0]
                }} transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 1
                }} className="absolute bottom-20 left-8 w-8 h-8 bg-primary-500/8 rounded-full" data-id="element-115" />
                  <div className="absolute inset-0 opacity-[0.03]" style={{
                  backgroundImage: `linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)`,
                  backgroundSize: '40px 40px'
                }} data-id="element-116" />
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8" data-id="element-117">
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center mb-5 shadow-2xl shadow-primary-500/30 ring-4 ring-primary-400/20" data-id="element-118">
                    <span className="text-white font-extrabold text-4xl" data-id="element-119">
                      СД
                    </span>
                  </div>
                  <h3 className="text-2xl font-extrabold text-white tracking-tight" data-id="element-120">
                    Сергій Денисюк
                  </h3>
                  <p className="text-primary-300 text-sm font-semibold mt-1.5" data-id="element-121">
                    {t.ceo.role}
                  </p>
                  <div className="mt-3 px-4 py-1.5 rounded-full bg-white/5 border border-white/10" data-id="element-122">
                    <p className="text-navy-300 text-xs font-medium tracking-wide" data-id="element-123">
                      ESCA — Executive Search Consulting Agency
                    </p>
                  </div>
                </div>
              </div>

              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={isInView ? {
              opacity: 1,
              scale: 1
            } : {}} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="absolute -bottom-4 right-0 sm:-bottom-5 sm:-right-5 bg-white rounded-2xl shadow-xl shadow-navy-200/40 border border-navy-100 p-4 sm:p-5" data-id="element-124">
                <div className="text-2xl sm:text-3xl font-extrabold bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent" data-id="element-125">
                  12+
                </div>
                <div className="text-xs text-navy-500 font-semibold mt-0.5" data-id="element-126">
                  {t.ceo.years_label}
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Content side */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="lg:col-span-3" data-id="element-127">
            <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-128">
              {t.ceo.eyebrow}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-129">
              Сергій Денисюк
            </h2>
            <p className="mt-2 text-lg text-navy-400 font-medium" data-id="element-130">
              {t.ceo.role}
            </p>

            <div className="mt-8 pl-6 border-l-4 border-primary-400/60" data-id="element-131">
              <blockquote className="text-xl sm:text-2xl text-navy-700 leading-relaxed font-medium italic" data-id="element-132">
                {t.ceo.quote}
              </blockquote>
            </div>

            <div className="mt-10 space-y-4" data-id="element-133">
              {t.ceo.points.map((point, index) => <div key={index} className="flex items-start gap-3" data-id="element-134">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm" data-id="element-135">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-136">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-137" />
                    </svg>
                  </div>
                  <p className="text-navy-600 leading-relaxed" data-id="element-138">
                    <strong className="text-navy-900" data-id="element-139">{point.title}</strong>{' '}
                    {point.description}
                  </p>
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}